import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { useNavigate } from 'react-router-dom';
import {
	OrderContext,
	OrderProps,
} from '../../../context/OrderContext/order.context';
import { QuantityPickerContext } from '../../../context/QuantityPickerContext/quantity-picker.context';
import { Geo } from '../../../interfaces/geo.interface';
import { MapMarker, PinVariant } from '../../../components/Map/Map.interface';
import { GeoService } from '../../../services/geo.services';
import { FileService } from '../../../services/file.service';
import { OrdersService } from '../../../services/orders.services';
import { T } from '@tolgee/react';
import useDebounce from '../../../hooks/useDebounce';
import SearchLocation from './components/SearchLocation';
import UploadLocationPhoto from './components/UploadLocationPhoto';
import Map from '../../../components/Map/Map';
import { MutatingDots } from 'react-loader-spinner';
import Flag from 'react-world-flags';
import { AuthContext } from '../../../context/context';
import { motion } from 'framer-motion';
import { cn } from '../../../lib/utils';
import { LatLng } from 'leaflet';
import {
	Contract,
	ContractsService,
} from '../../../services/contracts.service';
import { BlueBox } from '../../../assets/BlueBox';
import { AdminContracts } from '../../../assets/Contracts/AdminContracts';
import AddressesClock from '../../../assets/AddressesClock';
import { NumericFormat } from 'react-number-format';
import SelectOrderType from './components/SelectOrderType';
import { SelectOrderTypeOnSavePayload } from './clientSelectLocation.interface';
import { numberTwoDecimal, toNumber } from '../../../utils/number.util';
import { OrderInterface } from '../../../components/OrdersTable/interfaces/Order.interface';

const ClientSelectLocation: React.FC = () => {
	const navigate = useNavigate();
	const language = localStorage.getItem('lang') || 'en';
	const { user } = useContext(AuthContext);
	const { order, setOrder, setAdditional } = useContext(OrderContext);
	const { setInputQuantity, setTotalQuantityCounter } = useContext(
		QuantityPickerContext,
	);
	const [search, setSearch] = useState<string>('');
	const [locations, setLocations] = useState<Geo[]>([]);
	const [locationsLoading, setLocationsLoading] = useState<boolean>(false);
	const [localRecentOrders, setLocalRecentOrders] = useState<OrderProps[]>([]);
	const [localRecentContracts, setLocalRecentContracts] = useState<Contract[]>(
		[],
	);
	const [showUpload, setUploadModal] = useState<boolean>(false);
	const [currentTab, setCurrentTab] = useState<string>('orders');
	const [markers, setMarkers] = useState<MapMarker[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingMapData, setIsLoadingMapData] = useState<boolean>(true);
	const [isMapFullScreen, setIsMapFullScreen] = useState<boolean>(false);
	const [previewLocation, setPreviewLocation] = useState<Geo | null>(null);
	const [hasSearched, setHasSearched] = useState<boolean>(false);
	const [isMapSelection, setIsMapSelection] = useState<boolean>(false);
	const [isLgUp, setIsLgUp] = useState(false);
	const [mapExactClickPosition, setMapExactClickPosition] =
		useState<LatLng | null>(null);
	const [showOrderType, setShowOrderType] = useState<boolean>(false);
	const [pendingRecentOrder, setPendingRecentOrder] =
		useState<OrderProps | null>(null);

	const searchQuery = useDebounce(search, 500);

	const toNum = (v: unknown): number | null => {
		const n =
			typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
		return Number.isFinite(n) ? n : null;
	};

	const MemoizedMap = useMemo(
		() =>
			React.memo(Map, (prevProps, nextProps) => {
				return (
					JSON.stringify(prevProps.markers) ===
						JSON.stringify(nextProps.markers) &&
					JSON.stringify(prevProps.polygons) ===
						JSON.stringify(nextProps.polygons) &&
					prevProps.target === nextProps.target &&
					prevProps.isMapFullScreen === nextProps.isMapFullScreen &&
					JSON.stringify(prevProps.selectedLocation) ===
						JSON.stringify(nextProps.selectedLocation)
				);
			}),
		[],
	);

	const selectedLocationSafe = useMemo(() => {
		const loc = previewLocation;
		if (!loc) return undefined;

		const lat = toNum(loc.latitude);
		const lng = toNum(loc.longitude);

		if (lat == null || lng == null) return undefined;

		return { ...loc, latitude: lat, longitude: lng };
	}, [previewLocation]);

	const mapVariants = {
		normal: {
			width: 662,
			height: 738,
			maxWidth: 'none',
			maxHeight: 'none',
			position: 'static',
		},
		fullscreen: {
			width: '100%',
			height: '100%',
			maxWidth: 1220,
			maxHeight: 1040,
		},
	};

	const getRecentOrders = async () => {
		setIsLoading(true);
		setIsLoadingMapData(true);
		try {
			const { data } = await OrdersService.getRecentOrdersByProducts({
				limit: 5,
				offset: 0,
			});
			if (!data.count) {
				setIsLoading(false);
				setIsLoadingMapData(false);
				return;
			}

			const markerPromises = data.items
				.filter((order) => order?.location?.locationId)
				.map(async (order) => {
					return {
						latitude: order.deliveryLocation?.coordinates[1],
						longitude: order.deliveryLocation?.coordinates[0],
						variant: PinVariant.pickup,
						orderProduct: order.product,
					} as MapMarker;
				});

			const newMarkers = (await Promise.all(markerPromises)).filter(
				Boolean,
			) as MapMarker[];
			setLocalRecentOrders(data.items || []);
			setMarkers(newMarkers);
		} catch (e) {
			console.error('Error loading recent orders:', e);
		} finally {
			setIsLoading(false);
			setIsLoadingMapData(false);
		}
	};

	const getRecentContracts = async () => {
		setIsLoading(true);
		setIsLoadingMapData(true);
		try {
			const { data } = await ContractsService.getRecentContracts({
				limit: 5,
				offset: 0,
			});
			if (!data.count) {
				setIsLoading(false);
				setIsLoadingMapData(false);
				return;
			}

			setLocalRecentContracts(data.items || []);
		} catch (e) {
			console.error('Error loading recent orders:', e);
		} finally {
			setIsLoading(false);
			setIsLoadingMapData(false);
		}
	};

	const handleLocationSearch = useCallback(async () => {
		if (isMapSelection) {
			return;
		}

		if (!searchQuery.length) {
			setLocations([]);
			setHasSearched(false);
			return;
		}
		setLocationsLoading(true);
		setHasSearched(true);
		try {
			const { data } = await GeoService.getAutosuggest({
				phrase: searchQuery,
				countryCode: user?.profile?.countryCode!,
				maxResults: '20',
			});
			setLocations(data);
		} catch (e) {
			console.error('Error searching locations:', e);
		} finally {
			setLocationsLoading(false);
		}
	}, [searchQuery, user?.profile?.countryCode, isMapSelection]);

	useEffect(() => {
		const mql = window.matchMedia('(min-width: 1024px)');
		const update = () => setIsLgUp(mql.matches);
		update();
		// Safari support
		if (mql.addEventListener) mql.addEventListener('change', update);
		else mql.addListener(update);
		return () => {
			if (mql.removeEventListener) mql.removeEventListener('change', update);
			else mql.removeListener(update);
		};
	}, []);

	useEffect(() => {
		getRecentOrders().then(() => {});
		getRecentContracts().then(() => {});
		setInputQuantity(0);
		setTotalQuantityCounter(undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePreviewLocation = async (location: Geo | null) => {
		if (!location) {
			setPreviewLocation(null);
			return;
		}

		const lat = toNum(location.latitude);
		const lng = toNum(location.longitude);

		if (lat != null && lng != null) {
			let enriched: any = { ...location, latitude: lat, longitude: lng };

			if (!enriched.countryCode && !enriched.address?.countryCode) {
				try {
					const { data } = await GeoService.reverseGeocode({
						lat: String(lat),
						lng: String(lng),
						countryCode: user?.profile?.countryCode,
					});

					const first = data?.items?.[0];
					if (first?.address) {
						enriched = {
							...enriched,
							address: {
								...first.address,
								label: enriched.address?.label || first.address.label,
							},
							countryCode: first.address.countryCode,
							countryName: first.address.countryName,
						};
					}
				} catch (e) {
					console.error('Error enriching location (preview):', e);
				}
			}

			setPreviewLocation(enriched);
			return;
		}

		try {
			const { data: locationData } = await GeoService.getLocationDetails({
				locationid: location.locationId!,
			});

			const latN = toNum(locationData.Latitude);
			const lngN = toNum(locationData.Longitude);

			let enriched: any = {
				...location,
				latitude: latN ?? undefined,
				longitude: lngN ?? undefined,
			};

			if (latN != null && lngN != null) {
				try {
					const { data } = await GeoService.reverseGeocode({
						lat: String(latN),
						lng: String(lngN),
						countryCode: user?.profile?.countryCode,
					});

					const first = data?.items?.[0];
					if (first?.address) {
						enriched = {
							...enriched,
							address: first.address,
							countryCode: first.address.countryCode,
							countryName: first.address.countryName,
						};
					}
				} catch (e) {
					console.error('Error enriching location (details fallback):', e);
				}
			}

			setPreviewLocation(enriched);
		} catch (e) {
			console.error('Error fetching preview location details:', e);
		}
	};

	const handleLocationSelect = (location: Geo) => {
		const override =
			isMapSelection && mapExactClickPosition
				? {
						latitude: mapExactClickPosition.lat,
						longitude: mapExactClickPosition.lng,
				  }
				: {};

		(async () => {
			let result: any = { ...location, ...override };

			const lat = toNum(result.latitude) ?? toNum(result.position?.lat);
			const lng = toNum(result.longitude) ?? toNum(result.position?.lng);

			if (lat != null && lng != null) {
				result.latitude = lat;
				result.longitude = lng;
			}

			if (
				!result.countryCode &&
				!result.address?.countryCode &&
				lat != null &&
				lng != null
			) {
				try {
					const { data } = await GeoService.reverseGeocode({
						lat: String(lat),
						lng: String(lng),
						countryCode: user?.profile?.countryCode,
					});

					const first = data?.items?.[0];
					if (first?.address) {
						result.address = {
							...first.address,
							label: result.address?.label || first.address.label,
						};
						result.countryCode = first.address.countryCode;
						result.countryName = first.address.countryName;
					}
				} catch (e) {
					console.error('Error enriching location (confirm):', e);
				}
			}

			if (pendingRecentOrder) {
				const baseOrder = pendingRecentOrder;

				const updatedOrder: OrderProps = {
					...baseOrder,
					location: result as Geo,
				};

				setOrder(updatedOrder);
				setPendingRecentOrder(null);

				navigate(
					`/store/groups/${baseOrder?.group?.id}/categories/${baseOrder?.category?.id}/products/${baseOrder?.product?.id}/quantity`,
				);

				return;
			}

			setOrder({ ...order, location: result as Geo });

			if (!localRecentContracts.length) {
				setAdditional((prev) => ({ ...prev, orderFlow: 'BASE' }));
				setOrder((prev) => ({ ...(prev || {}), contractData: undefined }));
				setShowOrderType(false);
				setUploadModal(true);
				return;
			}

			setShowOrderType(true);
		})();
	};

	const handleOrderTypeSave = async (payload: SelectOrderTypeOnSavePayload) => {
		if (payload.kind === 'simple') {
			setAdditional((prev) => ({ ...prev, orderFlow: 'BASE' }));
			setOrder((prev) => ({ ...(prev || {}), contractData: undefined }));
			setShowOrderType(false);
			setUploadModal(true);
			return;
		}

		try {
			const { data } = await ContractsService.getContractById(
				payload.contract.id,
				'customer',
			);

			setAdditional((prev) => ({
				...prev,
				orderFlow: 'CONTRACT_AFTER_LOCATION',
			}));

			setOrder((prev) => ({ ...(prev ?? {}), contractData: data }));
			setShowOrderType(false);
			navigate(
				`/store/groups/${payload.groupId}/categories/${payload.categoryId}/products/${payload.productId}/quantity`,
			);
		} catch (e) {}
	};

	const selectRecentOrder = (order: OrderProps) => {
		setPendingRecentOrder(order);

		const latFromDelivery = toNum(order?.deliveryLocation?.coordinates?.[1]);
		const lngFromDelivery = toNum(order?.deliveryLocation?.coordinates?.[0]);
		const baseLocation = order.location as any;

		if (latFromDelivery != null && lngFromDelivery != null) {
			const addressLabel = baseLocation?.label || order.deliveryAddress || '';

			const locationFromOrder: Geo & {
				fromRecentOrder?: boolean;
				locationName?: string;
			} = {
				locationId: baseLocation?.locationId,
				latitude: latFromDelivery,
				longitude: lngFromDelivery,
				address: baseLocation?.address,
				countryCode: baseLocation?.countryCode,
				label: addressLabel,
				locationName: addressLabel,
				matchLevel: (baseLocation as any)?.matchLevel ?? '',
				fromRecentOrder: true,
			};

			handlePreviewLocation(locationFromOrder).then(() => {});

			setSearch(addressLabel);
		} else if (baseLocation) {
			const addressLabel =
				(baseLocation as any).label || order.deliveryAddress || '';

			const locationWithFlag = {
				...(baseLocation as Geo),
				label: addressLabel,
				locationName: addressLabel,
				fromRecentOrder: true,
			} as Geo & { fromRecentOrder?: boolean; locationName?: string };

			handlePreviewLocation(locationWithFlag).then(() => {});

			setSearch(addressLabel);
		}

		setIsMapSelection(false);
		setMapExactClickPosition(null);
	};

	const selectRecentAddress = (location: Geo) => {
		setOrder({ location: location });
		navigate('/store/groups');
	};

	const handlePhotoUpload = async (file?: File) => {
		try {
			if (!file) {
				setOrder({ ...order, locationPhoto: undefined });
				setUploadModal(false);
				navigate('/store/groups');
				return;
			}

			const { data } = await FileService.uploadFiles([file]);
			setOrder({ ...order, locationPhoto: data.items?.[0]?.path });
			setUploadModal(false);
			navigate('/store/groups');
		} catch (e) {}
	};

	useEffect(() => {
		if (isMapSelection) {
			return;
		}
		handleLocationSearch().then(() => {});
	}, [searchQuery, handleLocationSearch, isMapSelection]);

	const RecentOrderCard = ({ order }: { order: OrderProps }) => (
		<button
			type="button"
			className="w-full h-fit flex gap-3 items-start bg-transparent border border-solid border-gray-10 rounded-10 p-1 transition-all hover:bg-[#FAFAFA] hover:border-gray-22 cursor-pointer"
			onClick={() => selectRecentOrder(order)}
		>
			<div
				style={{ backgroundImage: `url(${order?.product?.photoUrl})` }}
				className="flex flex-none w-[44px] h-[44px] bg-no-repeat bg-cover rounded-[8px] border border-solid border-gray-10"
				role="img"
			/>
			<div className="flex flex-col gap-2">
				<div className="h-[22px] flex gap-3 items-center">
					<SypacText variant="body-normal-medium">
						<p className="text-[14px] leading-[16px] text-gray-80">
							{order?.product?.translations?.find(
								(locale) => locale.language === language,
							)?.name || order?.product?.name}
						</p>
					</SypacText>

					{(order?.product?.type || order?.product?.size) && (
						<>
							<div className="w-[2px] h-[2px] bg-gray-60 rounded-full -mt-[2px]" />
							<SypacText variant="body-normal-medium">
								<p className="text-[14px] leading-[16px] text-gray-80">
									{order?.product?.type}{' '}
									{order?.product?.size && `(${order?.product?.size})`}
								</p>
							</SypacText>
						</>
					)}
				</div>

				<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
					<SypacText variant="body-regular-medium">
						<p className="text-[12px] leading-[14px] text-gray-40">
							<T keyName="clientSelectLocation.productAddress">
								Product address:
							</T>
						</p>
					</SypacText>

					<div className="flex items-start gap-2 justify-self-start">
						{order?.location?.countryCode ? (
							<span className="-mt-[2px]">
								<Flag
									className="object-cover border border-solid border-gray-10 rounded"
									code={order?.location.countryCode}
									width={20}
									height={14}
								/>
							</span>
						) : null}
						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 text-[12px] leading-[14px] text-left">
								{order?.location?.label || ''}
							</p>
						</SypacText>
					</div>
				</div>

				{order.producer && (
					<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
						<SypacText variant="body-regular-medium">
							<p className="text-[12px] leading-[14px] text-gray-40">
								<T keyName="clientSelectLocation.sellerName">Seller name:</T>
							</p>
						</SypacText>

						<SypacText variant="body-regular-medium">
							<p className="text-gray-80 text-[12px] leading-[14px] text-left">
								{order.producer.company?.name || ''}
							</p>
						</SypacText>
					</div>
				)}

				{order.trucks && (
					<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
						<SypacText variant="body-regular-medium">
							<p className="text-[12px] leading-[14px] text-gray-40">
								<T keyName="clientSelectLocation.orderedQuantity">
									Ordered quantity:
								</T>
							</p>
						</SypacText>

						<SypacText variant="body-regular-medium">
							<p className="text-[12px] leading-[14px] text-gray-80">
								<NumericFormat
									type="text"
									displayType="text"
									thousandSeparator=" "
									decimalSeparator="."
									value={order.trucks[0].payload || 0}
								/>{' '}
								<T keyName="basicDetails.tons">tons</T>
							</p>
						</SypacText>
					</div>
				)}
			</div>
		</button>
	);

	const renderRecentAddresses = () => {
		const addressCounts = localRecentOrders.reduce<
			Record<
				string,
				{
					address: string;
					count: number;
					countryCode?: string;
					locationId: string;
				}
			>
		>((acc, order) => {
			const locationId = order?.location?.locationId;
			if (!locationId) return acc;

			const address = order?.deliveryAddress;

			if (!address) return acc;
			if (acc[locationId]) {
				acc[locationId].count += 1;
			} else {
				acc[locationId] = {
					address,
					count: 1,
					countryCode: order.location?.countryCode,
					locationId,
				};
			}

			return acc;
		}, {} as Record<string, { address: string; count: number; countryCode?: string; locationId: string }>);

		return Object.values(addressCounts).map(
			({ address, count, countryCode, locationId }) => (
				<button
					key={address}
					type="button"
					onClick={() =>
						selectRecentAddress(
							localRecentOrders.find(
								(l) => l.location?.locationId === locationId,
							)?.location!,
						)
					}
					className="w-full h-fit flex gap-3 items-center bg-transparent border border-solid border-gray-10 rounded-10 px-[10px] py-[10.5px] transition-all hover:bg-[#FAFAFA] hover:border-gray-22 cursor-pointer"
				>
					<div className="w-full flex gap-3 items-center">
						<span className="">
							<Flag
								className="object-cover border border-solid border-gray-10 rounded"
								code={countryCode}
								width={20}
								height={14}
							/>
						</span>
						<SypacText variant="body-regular-medium">
							<p className="text-[14px] leading-[16px] text-left text-gray-80">
								{address}
							</p>
						</SypacText>

						{count > 1 ? (
							<div className="flex items-center gap-5 ml-auto">
								<SypacText variant="body-regular-medium">
									<p className="text-[14px] leading-[16px] text-gray-40">
										<T keyName="clientSelectLocation.deliveries">Deliveries:</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-[14px] leading-[16px] text-gray-80">
										{count}
									</p>
								</SypacText>
							</div>
						) : null}
					</div>
				</button>
			),
		);
	};

	const getExecutedQtyForOrder = (order: any) => {
		const trucks = order?.trucks ?? [];

		if (trucks.length > 1) {
			return trucks.reduce(
				(sum: number, tr: any) =>
					sum + toNumber(tr?.loadedQuantity ?? tr?.payload),
				0,
			);
		}

		if (trucks.length === 1) {
			const tr = trucks[0];
			const hasLoaded =
				tr?.loadedQuantity !== undefined && tr?.loadedQuantity !== null;

			return hasLoaded
				? toNumber(tr.loadedQuantity)
				: toNumber(order?.quantity);
		}

		return toNumber(order?.quantity);
	};

	const getRemainingQtyTonsForContract = (contract: any, line: any) => {
		const contractQty = toNumber(line?.contractQtyTons);
		const rawOrders = (contract as any)?.orders;
		const contractOrders: OrderInterface[] = Array.isArray(rawOrders)
			? rawOrders
			: Array.isArray(rawOrders?.items)
			? rawOrders.items
			: Array.isArray(rawOrders?.data)
			? rawOrders.data
			: [];

		const executedQtyTotal = contractOrders.reduce(
			(sum: number, o: any) => sum + getExecutedQtyForOrder(o),
			0,
		);

		return contractQty - executedQtyTotal;
	};

	const renderRecentContracts = () => {
		return localRecentContracts?.map((contract) =>
			contract.lines?.map((line) => {
				const productId = line?.product?.id ?? line?.productId;
				const categoryId =
					line?.product?.categoryId ?? line?.product?.category?.id;
				const remainingQtyTons = getRemainingQtyTonsForContract(contract, line);

				return (
					<button
						key={line.id}
						type="button"
						className="w-full h-fit flex gap-3 items-start bg-transparent border border-solid border-gray-10 rounded-10 p-1 transition-all hover:bg-[#FAFAFA] hover:border-gray-22 cursor-pointer"
						onClick={async () => {
							if (!productId || !categoryId) return;

							try {
								const { data } = await ContractsService.getContractById(
									contract.id,
									'customer',
								);

								setAdditional((prev) => ({
									...prev,
									orderFlow: 'CONTRACT_LOCATION_FIRST',
								}));

								setOrder((prev) => {
									return {
										...(prev ?? {}),
										contractData: data,
										...(line?.product ? { product: line.product } : {}),
										location: undefined,
									} as typeof prev;
								});

								navigate('/store/location');
							} catch (e) {}
						}}
					>
						<div
							style={{ backgroundImage: `url(${line?.product?.photoUrl})` }}
							className="flex flex-none w-[44px] h-[44px] bg-no-repeat bg-cover rounded-[8px] border border-solid border-gray-10"
							role="img"
						/>
						<div className="flex flex-col gap-2">
							<div className="h-[22px] flex items-center gap-3">
								<SypacText variant="body-normal-medium">
									<p className="text-[14px] leading-[16px] text-gray-80">
										{line.product?.translations?.find(
											(locale) => locale.language === language,
										)?.name || line.product?.name}
									</p>
								</SypacText>
								{(line?.product?.type || line?.product?.size) && (
									<>
										<div className="w-[2px] h-[2px] bg-gray-60 rounded-full -mt-[2px]" />
										<SypacText variant="body-normal-medium">
											<p className="text-[14px] leading-[16px] text-gray-80">
												{line?.product?.type}{' '}
												{line?.product?.size && `(${line?.product?.size})`}
											</p>
										</SypacText>
									</>
								)}
							</div>

							<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[14px] text-gray-40">
										<T keyName="clientSelectLocation.productAddress">
											Product address:
										</T>
									</p>
								</SypacText>

								<div className="flex items-start gap-2 justify-self-start">
									{contract?.lines?.[0].product.countryCode ? (
										<span className="-mt-[2px]">
											<Flag
												className="object-cover border border-solid border-gray-10 rounded"
												code={contract?.lines?.[0].product.countryCode}
												width={20}
												height={14}
											/>
										</span>
									) : null}
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 text-[12px] leading-[14px] text-left">
											{contract?.lines?.[0].product.address || ''}
										</p>
									</SypacText>
								</div>
							</div>

							<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[14px] text-gray-40">
										<T keyName="clientSelectLocation.sellerName">
											Seller name:
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-80 text-[12px] leading-[14px] text-left">
										{contract.seller?.name || ''}
									</p>
								</SypacText>
							</div>

							<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[14px] text-gray-40">
										<T keyName="contractsTable.contractQuantity">
											Contract quantity:
										</T>
									</p>
								</SypacText>

								<SypacText variant="body-regular-medium">
									<p className="text-[12px] leading-[14px] text-gray-80">
										<NumericFormat
											type="text"
											displayType="text"
											thousandSeparator=" "
											decimalSeparator="."
											value={line.contractQtyTons}
										/>{' '}
										<T keyName="basicDetails.tons">tons</T>
									</p>
								</SypacText>
							</div>

							{numberTwoDecimal(remainingQtyTons) > 0 && (
								<div className="grid grid-cols-[140px,auto] items-start justify-items-start">
									<SypacText variant="body-regular-medium">
										<p className="text-[12px] leading-[14px] text-gray-40">
											<T keyName="contractsTable.remainingQuantity">
												Remaining quantity:
											</T>
										</p>
									</SypacText>

									<SypacText variant="body-regular-medium">
										<p className="text-[12px] leading-[14px] text-gray-80">
											<NumericFormat
												type="text"
												displayType="text"
												thousandSeparator=" "
												decimalSeparator="."
												value={numberTwoDecimal(remainingQtyTons)}
											/>{' '}
											<T keyName="basicDetails.tons">tons</T>
										</p>
									</SypacText>
								</div>
							)}
						</div>
					</button>
				);
			}),
		);
	};

	const EmptyState = ({
		type,
	}: {
		type: 'orders' | 'addresses' | 'contracts';
	}) => (
		<div className="h-full flex justify-center items-center">
			<div className="w-[339px] h-[150px] flex flex-col justify-between text-center">
				<div className="flex flex-col gap-4">
					<SypacText variant="body-regular-large">
						<p className="text-xl text-gray-80">
							<T
								keyName={
									type === 'contracts'
										? 'clientSelectLocation.noRecentContractsYet'
										: type === 'orders'
										? 'clientSelectLocation.noRecentOrdersYet'
										: 'selectLocation.noRecentAddresses'
								}
							>
								{type === 'contracts'
									? 'No recent contracts yet'
									: type === 'orders'
									? 'No recent orders yet'
									: 'No recent addresses'}
							</T>
						</p>
					</SypacText>
					<SypacText variant="body-regular-large">
						<p className="text-base text-gray-40">
							<T
								keyName={
									type === 'contracts'
										? 'clientSelectLocation.youHaveNoRecentContracts'
										: type === 'orders'
										? 'selectLocation.youHaveNoRecentOrders'
										: 'selectLocation.youHaveNoRecentAddresses'
								}
							>
								{type === 'contracts'
									? 'You have no recent contracts. Your contract lines will show up here for convenient reordering.'
									: type === 'orders'
									? 'You have no recent orders. Your previous orders will show up here for convenient reordering.'
									: 'You have no recent addresses. Your previous addresses will show up here for convenient reordering.'}
							</T>
						</p>
					</SypacText>
				</div>
			</div>
		</div>
	);

	return (
		<div className="flex lg:w-[calc(100vw-87px)] xl-2xl:w-[calc(100vw-104px)] h-full xl-2xl:justify-center 2xl:items-center mt-[68px] lg:mt-0">
			<section className="w-[556px] h-[calc(100vh-80px)] lg:h-auto 2xl:h-[738px] flex flex-col gap-6 xl:gap-[32px] xl-2xl:gap-10 2xl:gap-[60px] lg:px-5 xl-2xl:p-0 xl-2xl:pr-5">
				<div className="flex items-center gap-5">
					<SypacText variant="body-regular-large" className="px-3 lg:p-0">
						<p className="text-2xl xl:text-[32px] xl-2xl:text-[40px] leading-[42px] tracking-tighter text-gray-80 mt-6 lg:mt-0">
							<T keyName="clientSelectLocation.welcomeToThe">Welcome to the</T>
						</p>
					</SypacText>

					<div className="flex items-center gap-3">
						<Flag
							className="w-[44px] h-[32px] object-cover border border-solid border-gray-10 rounded-[8px] -mt-[5px]"
							code={user?.profile?.countryCode}
							width={44}
							height={32}
						/>

						<SypacText variant="body-regular-large" className="px-3 lg:p-0">
							<p className="text-2xl xl:text-[32px] xl-2xl:text-[40px] leading-[42px] tracking-tighter text-gray-80 mt-6 lg:mt-0">
								<T keyName="clientSelectLocation.sypacStore">Sypac store!</T>
							</p>
						</SypacText>
					</div>
				</div>

				<div className="h-full flex flex-col gap-3 xl:gap-[32px] xl-2xl:gap-[43px]">
					<div className="flex flex-col gap-4 xl:gap-6 px-3 lg:p-0">
						<SypacText variant="body-regular-large">
							<p className="text-base xl:text-xl text-gray-80">
								<T keyName="clientSelectLocation.whereToDeliverYourGoods">
									Where to deliver your goods?
								</T>
							</p>
						</SypacText>
						<SearchLocation
							inputName="search_location"
							locations={locations}
							locationsLoading={locationsLoading}
							hasSearched={hasSearched}
							search={search}
							selectedLocation={previewLocation}
							onChange={(value) => {
								setSearch(value);
								setIsMapSelection(false);
								setMapExactClickPosition(null);
								setPendingRecentOrder(null);
							}}
							selectLocation={handleLocationSelect}
							onPreviewLocation={handlePreviewLocation}
						/>
					</div>

					<div className="w-full flex flex-col h-[calc(100%-106px)] 2xl:h-[480px] border border-solid border-gray-10 lg:rounded-10 bg-white overflow-hidden box-border">
						<div className="flex gap-1 p-2 rounded-t-10 border border-solid border-gray-10 border-t-0 border-l-0 border-r-0 bg-white box-border">
							<SypacButton variant="secondary" size="small" className="w-full">
								<button
									type="button"
									className={cn(
										'w-full h-[42px] lg:h-[32px] rounded-[6px] flex gap-2 items-center px-1 border border-solid border-transparent transition-all group whitespace-nowrap',
										currentTab === 'orders'
											? 'bg-primary-violet text-white border-transparent'
											: 'hover:bg-[#FAFAFA] hover:border-[#E8E8E8] text-gray-80',
									)}
									onClick={() => setCurrentTab('orders')}
								>
									<BlueBox
										width={16}
										height={16}
										color={currentTab === 'orders' ? '#FFF' : '#454545'}
									/>
									<SypacText variant="body-regular-medium">
										<p className="text-[16px] leading-[22px] mt-1">
											<T keyName="clientSelectLocation.myRecentOrders">
												My recent orders
											</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>

							<SypacButton variant="secondary" size="small" className="w-full">
								<button
									type="button"
									className={cn(
										'w-full h-[42px] lg:h-[32px] rounded-[6px] flex gap-2 items-center px-1 border border-solid border-transparent transition-all group whitespace-nowrap',
										currentTab === 'addresses'
											? 'bg-primary-violet text-white border-transparent'
											: 'hover:bg-[#FAFAFA] hover:border-[#E8E8E8] text-gray-80',
									)}
									onClick={() => setCurrentTab('addresses')}
								>
									<AddressesClock
										color={currentTab === 'addresses' ? '#FFF' : '#454545'}
									/>
									<SypacText variant="body-regular-medium">
										<p className="text-[16px] leading-[22px] mt-1">
											<T keyName="clientSelectLocation.recentAddresses">
												Recent addresses
											</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>

							<SypacButton variant="secondary" size="small" className="w-full">
								<button
									type="button"
									className={cn(
										'w-full h-[42px] lg:h-[32px] rounded-[6px] flex gap-2 items-center px-1 border border-solid border-transparent transition-all group whitespace-nowrap',
										currentTab === 'contracts'
											? 'bg-primary-violet text-white border-transparent'
											: 'hover:bg-[#FAFAFA] hover:border-[#E8E8E8] text-gray-80',
									)}
									onClick={() => setCurrentTab('contracts')}
								>
									<AdminContracts
										width={17}
										height={17}
										color={currentTab === 'contracts' ? '#FFF' : '#454545'}
									/>
									<SypacText variant="body-regular-medium">
										<p className="text-[16px] leading-[22px] mt-1">
											<T keyName="clientSelectLocation.recentContracts">
												My contracts
											</T>
										</p>
									</SypacText>
								</button>
							</SypacButton>
						</div>

						{currentTab === 'orders' ? (
							isLoading ? (
								<div className="flex h-full items-center justify-center">
									<MutatingDots
										height="100"
										width="100"
										color="#7693F4"
										secondaryColor="#494C83"
										radius="12.5"
										ariaLabel="mutating-dots-loading"
										visible
									/>
								</div>
							) : localRecentOrders.length !== 0 ? (
								<div className="w-full h-full flex flex-col p-2 overflow-x-hidden overflow-y-scroll scroll-smooth scrollbar-custom box-border">
									<div className="flex flex-col gap-[6px]">
										{localRecentOrders.map((order: OrderProps) => (
											<RecentOrderCard key={order?.product?.id} order={order} />
										))}
									</div>
								</div>
							) : (
								<EmptyState type="orders" />
							)
						) : null}

						{currentTab === 'addresses' ? (
							localRecentOrders.length !== 0 ? (
								<div className="w-full h-full flex flex-col p-2 overflow-y-scroll scroll-smooth scrollbar-custom box-border">
									<div className="flex flex-col gap-3" key={order?.product?.id}>
										{renderRecentAddresses()}
									</div>
								</div>
							) : (
								<EmptyState type="addresses" />
							)
						) : null}

						{currentTab === 'contracts' ? (
							localRecentContracts.length !== 0 ? (
								<div className="w-full h-full flex flex-col p-2 overflow-y-scroll scroll-smooth scrollbar-custom box-border">
									<div className="flex flex-col gap-3" key={order?.product?.id}>
										{renderRecentContracts()}
									</div>
								</div>
							) : (
								<EmptyState type="contracts" />
							)
						) : null}
					</div>
				</div>
			</section>

			<motion.section
				variants={mapVariants}
				initial="normal"
				animate={isMapFullScreen ? 'fullscreen' : 'normal'}
				transition={{ type: 'spring', stiffness: 200, damping: 30 }}
				className={`${isMapFullScreen ? '' : 'hidden lg:flex'} overflow-hidden`}
			>
				{!isLoadingMapData && (isMapFullScreen || isLgUp) && (
					<MemoizedMap
						polygons={[]}
						markers={markers}
						target="customer"
						isMapFullScreen={isMapFullScreen}
						setIsMapFullScreen={setIsMapFullScreen}
						selectedLocation={selectedLocationSafe}
						onConfirmLocation={handleLocationSelect}
						onReversePin={(geo: any) => {
							setIsMapSelection(true);

							const addressLabel = geo.address?.label || '';
							setSearch(addressLabel);

							const lat = toNum(geo.latitude);
							const lng = toNum(geo.longitude);

							const reverseGeoLocation: Geo = {
								locationId: geo.locationId,
								latitude: lat ?? undefined,
								longitude: lng ?? undefined,
								address: geo.address,
								countryCode: geo.countryCode,
								label: '',
								matchLevel: '',
							};

							setPreviewLocation(
								lat != null && lng != null ? reverseGeoLocation : null,
							);
							setLocations([]);
							setHasSearched(false);

							setMapExactClickPosition(geo.exactClickPosition ?? null);
						}}
						fitMarkersToBounds
					/>
				)}
			</motion.section>

			<SelectOrderType
				isOpen={showOrderType}
				onClose={() => setShowOrderType(false)}
				onSave={handleOrderTypeSave}
				contracts={localRecentContracts}
			/>
			<UploadLocationPhoto
				isOpen={showUpload}
				onClose={() => setUploadModal(false)}
				onSave={handlePhotoUpload}
			/>
		</div>
	);
};

export default ClientSelectLocation;
